import type { Visit } from './reports/place';

/** "Today", "Yesterday", or the date. Pure. */
export function dayLabel(at: number, now: number = Date.now()): string {
  const day = (t: number) => new Date(t).toDateString();
  if (day(at) === day(now)) return 'Today';
  if (day(at) === day(now - 86_400_000)) return 'Yesterday';
  return new Date(at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Where you have been in this room: each company, product, report tab and card
 * you opened, newest first, grouped by day. Click one to go back to it.
 *
 * The body of the History panel on the right-hand rail.
 */
export default function HistoryList({
  visits,
  onPick,
  onClear,
}: {
  visits: Visit[];
  onPick: (v: Visit) => void;
  onClear: () => void;
}) {
  let lastDay = '';
  if (visits.length === 0) {
    return (
      <p className="p-3 text-xs text-gray-500 dark:text-gray-400" data-testid="history-panel">
        Nothing yet. Every company, product and report you open shows up here.
      </p>
    );
  }
  return (
    <div className="p-2 text-xs" data-testid="history-panel">
      {/* SAYS WHAT IT IS. A list of names and times with nothing above it read
          as a log of something the room did. */}
      <p className="px-1 pb-1 text-gray-500 dark:text-gray-400" data-testid="history-what">
        Places you opened in this room, newest first. Click one to go back to it.
      </p>
      <ul>
        {visits.map((v, i) => {
          const day = dayLabel(v.at);
          const head = day !== lastDay;
          lastDay = day;
          return (
            <li key={`${v.key}-${v.at}-${i}`}>
              {head && <div className="px-1 pt-2 pb-0.5 text-[11px] uppercase tracking-wide text-gray-400">{day}</div>}
              <button
                type="button"
                onClick={() => onPick(v)}
                className="w-full flex items-baseline gap-2 rounded px-1 py-0.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                data-testid="history-entry"
              >
                <span className="shrink-0 w-16 whitespace-nowrap tabular-nums text-gray-400">
                  {new Date(v.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </span>
                <span className="flex-1 min-w-0 truncate text-gray-800 dark:text-gray-100">{v.label}</span>
                <span className="shrink-0 text-[11px] text-gray-400">{v.what}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-1 border-t border-gray-100 dark:border-gray-700 pt-1 text-right">
        <button type="button" onClick={onClear} className="text-xs text-gray-500 hover:underline" data-testid="history-clear">
          Clear history
        </button>
      </div>
    </div>
  );
}
